import { Auth } from '@d-id/client-sdk';
import styled from '@emotion/styled';
import { useMemo } from 'react';
import { PreviewType } from '../../CreatePage/types';
import { PublicCampaign, UpdateCampaignSessionPayload } from '../types';
import { getScriptWithInput } from '../utils';
import { Agent } from './Agent';
import { Card } from './Card';
import { CtaButton } from './CtaButton';
import { Script } from './Script';

const Title = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 24px;
    font-style: normal;
    font-weight: 800;
    margin-block-start: 16px;
    margin-block-end: 11px;

    font-weight: 800;
    line-height: 28px;
`;

const SubTitle = styled.span`
    color: ${({ theme }) => theme.colors.black[800]};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 21px;
    text-align: center;
    max-width: 560px;
`;

const StyledLogo = styled.img`
    max-height: 100px;
    max-width: 300px;
    object-fit: contain;
    margin-block-end: 32px;
`;

interface Props {
    campaign: PublicCampaign;
    inputFields: Record<string, string>;
    sendAnalytic: (data: UpdateCampaignSessionPayload) => void;
    auth: Auth;
    preview?: PreviewType;
}

export const EmbeddedCampaignCard = ({ campaign, inputFields, sendAnalytic, auth, preview }: Props) => {
    const script = useMemo(
        () =>
            getScriptWithInput({
                script: campaign.script,
                inputFields,
                scriptFields: campaign.script_fields,
                campaignId: campaign.id,
            }),
        [inputFields, campaign.script, campaign.script_fields, campaign.id]
    );

    const showAgent = !!script;

    return (
        showAgent && (
            <Agent
                agentId={campaign.agent_id}
                script={script}
                auth={auth}
                sendAnalytic={sendAnalytic}
                preview={preview}
                duration={campaign.duration}
                embed={true}
            />
        )
    );
};

export const CampaignCard = ({ campaign, inputFields, sendAnalytic, auth, preview }: Props) => {
    const haveEnoughCredits = true; // TODO: implement logic

    const script = useMemo(
        () =>
            getScriptWithInput({
                script: campaign.script,
                inputFields,
                scriptFields: campaign.script_fields,
                campaignId: campaign.id,
            }),
        [inputFields, campaign.script, campaign.script_fields, campaign.id]
    );

    const showAgent = haveEnoughCredits && script;
    const showScript = !haveEnoughCredits && script;

    return (
        <Card preview={preview}>
            {campaign.logo_url && <StyledLogo src={campaign.logo_url} alt="logo" />}
            {showAgent && (
                <Agent
                    agentId={campaign.agent_id}
                    script={script}
                    auth={auth}
                    sendAnalytic={sendAnalytic}
                    preview={preview}
                    duration={campaign.duration}
                />
            )}
            {campaign.title && <Title>{campaign.title}</Title>}
            {campaign.description && <SubTitle>{campaign.description}</SubTitle>}
            {showScript && <Script script={script} />}
            <CtaButton
                enabled={campaign.cta_enabled}
                text={campaign.cta_text}
                url={campaign.cta_url}
                backgroundColor={campaign.cta_background_color}
                onClick={() => sendAnalytic({ cta_clicked: true })}
            />
        </Card>
    );
};
